import React, { useEffect, useState } from "react";
import { PageLayout } from "../components/page-layout";
import { runScraper } from "../services/scraper.service";
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import { TableContainer, Paper, List, ListItem, ListItemText, Button, TextField, Alert, Divider, Typography, Box } from "@mui/material";
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useSiteVarsFunctions } from "../hooks/use-site-vars-functions";

export const ScraperPage = () => {
  const { getAllowScrape, getScoresUpdatedTime, formatGameWeek } = useSiteVarsFunctions();
  const scrapingEnabled = getAllowScrape();
  const currentGameWeek = formatGameWeek();
  const scoresUpdatedTime = getScoresUpdatedTime();
  const [lastUpdated, setLastUpdated] = useState("");
  const [scraperRunning, setScraperRunning] = useState(false);
  const [freeTextInput, setFreeTextInput] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  
  const processPayload = async (rawJsonText, sourceName = "data") => {
    try {
      const scoresJson = typeof rawJsonText === "object" ? rawJsonText : JSON.parse(rawJsonText);
      if (scoresJson && scoresJson.events && scoresJson.events.length > 0 && scoresJson.elements && scoresJson.elements.length > 500) { 
        let currentWeek = 0;
        for (let i = 0; i < scoresJson.events.length; i++) {
          const event = scoresJson.events[i];
          if (event.is_current === true) {
            currentWeek = event.id;
            break;
          }
        }
        
        // If pre-season, use upcoming gameweek (is_next === true)
        if (currentWeek === 0) {
          for (let i = 0; i < scoresJson.events.length; i++) {
            const event = scoresJson.events[i];
            if (event.is_next === true) {
              currentWeek = event.id;
              break;
            }
          }
        }
        
        if (currentGameWeek.split(" ")[1] == currentWeek) {
          const payloadString = typeof rawJsonText === "string" ? rawJsonText : JSON.stringify(rawJsonText);
          const { data, error } = await runScraper(payloadString);
          if (error) {
            setErrorMessage(`Submission failed: ${error.message || JSON.stringify(error)}`);
          } else if (data) {
            const writeMsg = data.updatedPlayersCount !== undefined
              ? `Updated ${data.updatedPlayersCount} players (saved ${data.writesSaved} unnecessary writes).`
              : "Scores successfully updated!";
            const gwMsg = data.rolledOver ? ` Gameweek rolled over to Week ${data.currentWeek}!` : "";
            setSuccessMessage(`Success (${sourceName})! ${writeMsg}${gwMsg}`);
            setLastUpdated(new Date().toLocaleString());
            setFreeTextInput("");
          }
        } else {
          setErrorMessage(`Can't update scores as the TLOG game week (${currentGameWeek.split(" ")[1]}) does not match the Premier League game week (${currentWeek})`);
        }
      } else {
        setErrorMessage(`The provided FPL data is not valid (must contain events and 500+ players)`);
      }
    } catch (err) {
      setErrorMessage(`Invalid JSON format: ${err.message}`);
    }
  };

  const handlePasteFromClipboard = async () => {
    setErrorMessage("");
    setSuccessMessage("");
    setScraperRunning(true);

    try {
      if (!navigator.clipboard || !navigator.clipboard.readText) {
        setErrorMessage("Clipboard API not supported in your browser. Please paste into the text box below.");
        setScraperRunning(false);
        return;
      }
      const text = await navigator.clipboard.readText();
      if (!text || text.trim().length === 0) {
        setErrorMessage("Your clipboard is empty. Please copy the FPL data first.");
        setScraperRunning(false);
        return;
      }
      await processPayload(text, "Clipboard");
    } catch (err) {
      setErrorMessage(`Failed to read clipboard: ${err.message}. Please paste into the box below.`);
    }

    setScraperRunning(false);
  };

  const handleRunScraper = async () => {
    setErrorMessage("");
    setSuccessMessage("");
    setScraperRunning(true);
    await processPayload(freeTextInput, "Manual input");
    setScraperRunning(false);
  };

  useEffect(() => {
    setLastUpdated(scoresUpdatedTime && scoresUpdatedTime._seconds ? new Date(scoresUpdatedTime._seconds * 1000 + scoresUpdatedTime._nanoseconds / 1000000).toLocaleString() : "Never");
  }, [scoresUpdatedTime]);

  return (
    <PageLayout>
      {errorMessage && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage}</Alert>}
      {successMessage && <Alert severity="success" sx={{ mb: 2 }}>{successMessage}</Alert>}
      <TableContainer component={Paper} sx={{ p: 2, maxWidth: 650 }}>
      {lastUpdated.length === 0 && (
        <Stack spacing={1}>
          <Skeleton variant="rounded" width={"100%"} height={26} />
          <Skeleton variant="rounded" width={"100%"} height={26} />
          <Skeleton variant="rounded" width={"100%"} height={26} />
        </Stack>
      )}
      {lastUpdated.length > 0 && (
        <List dense sx={{ width: '100%', bgcolor: 'background.paper' }}>
            <ListItem>
                <ListItemText
                primary="Cloud Automation"
                secondary="Automated: Running hourly via GitHub Actions (07:00 – 23:00 UK time)"
                />
            </ListItem>
            <ListItem>
                <ListItemText
                primary="Current Gameweek"
                secondary={currentGameWeek}
                />
            </ListItem>
            <ListItem>
                <ListItemText
                primary="Last Updated"
                secondary={lastUpdated}
                />
            </ListItem>
            <ListItem>
                <ListItemText
                primary="Intelligent Optimization"
                secondary="Diff engine active: 0 Firestore writes & 0 reads when scores haven't changed"
                />
            </ListItem>
            
            <Divider sx={{ my: 2 }} />
            
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
              Manual On-Demand Sync
            </Typography>

            <Box sx={{ display: 'flex', gap: 1.5, flexDirection: 'column', my: 1 }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<ContentPasteIcon />}
                disabled={scraperRunning || !scrapingEnabled}
                onClick={handlePasteFromClipboard}
              >
                {scraperRunning ? "Processing..." : "1-Click: Paste from Clipboard & Scrape"}
              </Button>

              <Button
                variant="outlined"
                color="secondary"
                startIcon={<OpenInNewIcon />}
                component="a"
                href="https://fantasy.premierleague.com/api/bootstrap-static/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Open Official FPL Data in New Tab
              </Button>
            </Box>

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, mb: 2 }}>
              💡 <b>Fastest On-Demand Sync:</b> Click "Open Official FPL Data" &rarr; Press <kbd>Ctrl+A</kbd>, <kbd>Ctrl+C</kbd> &rarr; Click "1-Click: Paste from Clipboard & Scrape".
            </Typography>

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Manual Text Paste (Fallback)
            </Typography>
            <ListItem>
                <TextField fullWidth id="free-text-input" label="Or paste JSON scores data here" variant="outlined" multiline rows={2} value={freeTextInput} onChange={(e) => setFreeTextInput(e.target.value)} />
            </ListItem>
            <Box sx={{ mt: 1 }}>
              <Button variant="outlined" disabled={scraperRunning || !scrapingEnabled || !freeTextInput.trim()} onClick={handleRunScraper}>
                Submit Manual Text
              </Button>
            </Box>
        </List>
      )}
      
    </TableContainer>
    </PageLayout>
  );
};
